'use client';

import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { ArrowRight, X, Users, Loader2, CheckCircle, Building2, TrendingDown, AlertCircle, BarChart2 } from 'lucide-react';
import { accessRequestsService } from '@/lib/gemba/service';
import { useAuth } from '@/contexts/AuthContext';

export default function LandingPage() {
  const [formOpen, setFormOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; email?: string; company?: string; consent?: string }>({});

  const { user } = useAuth();

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    if (!root) return;
    const hadDark = root.classList.contains('dark');
    root.classList.remove('dark');
    return () => {
      if (hadDark) root.classList.add('dark');
    };
  }, []);

  function validate() {
    const e: { name?: string; email?: string; company?: string; consent?: string } = {};
    if (!name.trim()) e.name = 'Navn er påkrævet';
    if (!email.trim()) e.email = 'E-mail er påkrævet';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = 'Indtast en gyldig e-mail';
    if (!company.trim()) e.company = 'Virksomhed er påkrævet';
    if (!marketingConsent) e.consent = 'Du skal acceptere for at fortsætte';
    return e;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true);
    setSubmitError('');
    const ok = await accessRequestsService.submit({
      name: name.trim(),
      email: email.trim(),
      company: company.trim(),
      teamSize: teamSize || undefined,
      marketingConsent,
    });
    setLoading(false);
    if (ok) {
      setSubmitted(true);
    } else {
      setSubmitError('Noget gik galt. Prøv igen.');
    }
  }

  function openForm() {
    setFormOpen(true);
    setSubmitted(false);
    setName('');
    setEmail('');
    setCompany('');
    setTeamSize('');
    setMarketingConsent(false);
    setErrors({});
    setSubmitError('');
  }

  return (
    <div className="min-h-screen bg-[#FDFAF5] font-sans overflow-x-hidden">
      {/* Subtle background texture */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-blue-100/30 blur-3xl" />
        <div className="absolute bottom-0 -left-40 w-[400px] h-[400px] rounded-full bg-amber-100/25 blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] rounded-full bg-orange-50/40 blur-3xl" />
      </div>

      {/* ── NAV ── */}
      <nav className="relative z-10 max-w-5xl w-full mx-auto px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl overflow-hidden shadow-warm-sm">
            <Image
              src="/assets/images/GembaKonceptNoText-1773955908851.png"
              alt="TeamGemba logo"
              width={32}
              height={32}
              className="object-cover w-full h-full"
            />
          </div>
          <span className="font-bold text-[#1E2D4A] text-base tracking-tight">TeamGemba</span>
        </div>
        <div className="flex items-center gap-4">
          <Link
            href="/om"
            className="text-sm text-[#4A5568] hover:text-[#1E2D4A] font-medium transition-colors"
          >
            Om TeamGemba
          </Link>
          {user ? (
            <Link
              href="/dashboard"
              className="text-sm text-[#4A5568] hover:text-[#1E2D4A] font-medium transition-colors"
            >
              Gå til dashboard
            </Link>
          ) : (
            <Link
              href="/login"
              className="text-sm text-[#4A5568] hover:text-[#1E2D4A] font-medium transition-colors"
            >
              Log ind
            </Link>
          )}
          <button
            onClick={openForm}
            className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-white text-sm font-semibold rounded-xl transition-colors shadow-warm-sm"
          >
            Prøv TeamGemba
          </button>
        </div>
      </nav>

      {/* ── SECTION 1: HERO ── */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 pt-10 pb-14 text-center">
        <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200/60 text-blue-700 text-xs font-semibold px-3.5 py-1.5 rounded-full mb-6">
          <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
          Baseret på dansk forskning
        </div>

        <h1 className="text-4xl sm:text-5xl font-extrabold text-[#1E2D4A] leading-tight tracking-tight mb-4 max-w-3xl mx-auto">
          Når trivsel falder,{' '}
          <span className="relative inline-block">
            <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-orange-500">
              falder performance
            </span>
            <span className="absolute bottom-1 left-0 right-0 h-2 bg-amber-200/50 -z-0 rounded" />
          </span>
        </h1>

        <p className="text-base sm:text-lg text-sand-600 max-w-xl mx-auto leading-relaxed mb-8">
          Forskning viser, at trivsel og produktivitet hænger direkte sammen. TeamGemba gør det synligt – før det bliver et problem.
        </p>

        <button
          onClick={openForm}
          className="inline-flex items-center gap-2 px-8 py-4 bg-amber-500 hover:bg-amber-400 text-white text-sm font-bold rounded-2xl shadow-warm-lg hover:shadow-warm-xl hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
        >
          Prøv TeamGemba
          <ArrowRight size={16} />
        </button>
        <p className="mt-3 text-xs text-sand-400">Tidlig adgang</p>
      </section>

      {/* ── SECTION 2: STAT CARDS ── */}
      <section className="relative z-10 max-w-5xl mx-auto px-6 pb-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* Card 1 — Problem */}
          <div className="bg-white rounded-3xl border border-sand-200/60 p-6 shadow-warm hover:shadow-warm-lg hover:-translate-y-1 transition-all duration-200 group">
            <div className="w-10 h-10 rounded-2xl bg-amber-50 flex items-center justify-center mb-4 group-hover:bg-amber-100 transition-colors">
              <TrendingDown size={20} className="text-amber-600" />
            </div>
            <div className="text-xl font-bold text-[#1E2D4A] leading-snug mb-2">Dårlig trivsel skaber problemer</div>
            <p className="text-xs text-sand-500 leading-relaxed mb-4">
              Lav trivsel fører til mere stress, lavere produktivitet og flere sygedage
            </p>
            <a
              href="https://www.ae.dk/analyse/2024-03-stress-koster-55-mia-kr-om-aaret"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] text-sand-400 hover:text-sand-600 transition-colors underline"
            >
              Kilde: AE &amp; HK analyse (2024)
            </a>
          </div>

          {/* Card 2 — Data/Evidence */}
          <div className="bg-blue-50 rounded-3xl border border-blue-200/60 p-6 shadow-warm-lg hover:-translate-y-1 transition-all duration-200 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-blue-100/50 -translate-y-8 translate-x-8" />
            <div className="w-10 h-10 rounded-2xl bg-blue-100 flex items-center justify-center mb-4">
              <AlertCircle size={20} className="text-blue-600" />
            </div>
            <div className="text-xl font-bold text-[#1E2D4A] leading-snug mb-2">Stress koster Danmark 55 mia. kr. årligt</div>
            <p className="text-xs text-[#4A5568] leading-relaxed mb-4">
              Stressede medarbejdere arbejder mindre og er oftere syge — svarer til over 55.000 fuldtidsstillinger
            </p>
            <a
              href="https://www.ae.dk/analyse/2024-03-stress-koster-55-mia-kr-om-aaret"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] text-blue-400 hover:text-blue-600 transition-colors underline"
            >
              Kilde: Arbejderbevægelsens Erhvervsråd
            </a>
          </div>

          {/* Card 3 — Positive outcome */}
          <div className="bg-white rounded-3xl border border-sand-200/60 p-6 shadow-warm hover:shadow-warm-lg hover:-translate-y-1 transition-all duration-200 group">
            <div className="w-10 h-10 rounded-2xl bg-orange-50 flex items-center justify-center mb-4 group-hover:bg-orange-100 transition-colors">
              <BarChart2 size={20} className="text-orange-500" />
            </div>
            <div className="text-xl font-bold text-[#1E2D4A] leading-snug mb-2">Høj trivsel skaber bedre resultater</div>
            <p className="text-xs text-sand-500 leading-relaxed mb-4">
              Bedre trivsel reducerer sygefravær og øger engagement og performance
            </p>
            <a
              href="https://nfa.dk/nyt/nyheder/2025/december/bedre-psykisk-arbejdsmiljoe-kan-give-samfundet-en-aarlig-gevinst-paa-cirka-5-5-milliarder-kroner-om-aaret"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] text-sand-400 hover:text-sand-600 transition-colors underline"
            >
              Kilde: Det Nationale Forskningscenter for Arbejdsmiljø
            </a>
          </div>
        </div>

        {/* Insight note below cards */}
        <div className="mt-5 text-center">
          <p className="text-sm text-sand-500 italic">De fleste virksomheder opdager mistrivsel for sent</p>
        </div>
      </section>

      {/* ── SECTION 3: PROBLEM ── */}
      <section className="relative z-10 max-w-5xl mx-auto px-6 py-10">
        <div className="bg-amber-50/70 rounded-3xl border border-amber-200/40 px-8 py-10 sm:px-12 flex flex-col sm:flex-row items-start gap-6">
          <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center">
            <span className="text-2xl">🔍</span>
          </div>
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#1E2D4A] mb-3 leading-snug">
              Problemet er ikke, at virksomheder er ligeglade
            </h2>
            <p className="text-sand-600 leading-relaxed text-sm sm:text-base max-w-xl">
              De fleste opdager bare mistrivsel for sent. Når først det kan mærkes, er det allerede blevet dyrt.
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTION 4: SOLUTION ── */}
      <section className="relative z-10 max-w-5xl mx-auto px-6 pb-14">
        <div className="text-center mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1E2D4A] mb-2">Sådan fungerer det i praksis</h2>
          <p className="text-sand-500 text-sm">Tre enkle trin. Ingen kompleksitet.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { emoji: '📋', title: 'Ugentlige check-ins', desc: 'Få et ærligt billede af hvordan dit team har det', num: '01' },
            { emoji: '📈', title: 'Overblik', desc: 'Se trends og udvikling før det bliver et problem', num: '02' },
            { emoji: '🤖', title: 'AI Guide', desc: 'Få konkrete forslag til hvad du skal gøre', num: '03' },
          ].map((f) => (
            <div
              key={f.title}
              className="bg-white rounded-2xl border border-sand-200/60 p-6 shadow-warm-sm hover:shadow-warm transition-shadow"
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="text-2xl">{f.emoji}</span>
                <span className="text-xs font-bold text-sand-400 uppercase tracking-widest">{f.num}</span>
              </div>
              <h3 className="font-bold text-[#1E2D4A] mb-2 text-base">{f.title}</h3>
              <p className="text-xs text-sand-500 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── SECTION 5: IMPACT ── */}
      <section className="relative z-10 max-w-5xl mx-auto px-6 pb-14">
        <div className="bg-gradient-to-br from-blue-50 to-amber-50 rounded-3xl border border-blue-100/60 px-8 py-10 sm:px-12 text-center">
          <div className="text-3xl mb-4">✨</div>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1E2D4A] mb-4 leading-snug">
            Det er ofte de små ting, der gør den største forskel i et team.
          </h2>
          <p className="text-sand-600 leading-relaxed text-sm sm:text-base max-w-lg mx-auto">
            Når du opdager mistrivsel tidligt, kan du handle tidligt. Det skaber bedre teams, bedre samarbejde – og bedre resultater.
          </p>
        </div>
      </section>

      {/* ── SECTION 6: FINAL CTA ── */}
      <section className="relative z-10 max-w-5xl mx-auto px-6 pb-16">
        <div className="bg-blue-50 border border-blue-200/60 rounded-3xl px-8 py-12 sm:px-12 text-center relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-blue-100/40 -translate-y-24 translate-x-24" />
          <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full bg-amber-100/30 translate-y-16 -translate-x-16" />
          <div className="relative z-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1E2D4A] mb-3 leading-snug">
              Start med at forstå dit team
            </h2>
            <p className="text-[#4A5568] text-sm mb-8 max-w-sm mx-auto">
              Tidlig adgang til TeamGemba. Vi skriver til dig, når din plads er klar.
            </p>
            <button
              onClick={openForm}
              className="inline-flex items-center gap-2 px-8 py-4 bg-amber-500 hover:bg-amber-400 text-white text-sm font-bold rounded-2xl shadow-warm-lg hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
            >
              Prøv TeamGemba
              <ArrowRight size={16} />
            </button>
            <p className="mt-3 text-xs text-sand-400">Tidlig adgang</p>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="relative z-10 max-w-5xl mx-auto px-6 pb-8 flex items-center justify-between text-xs text-sand-400">
        <span>© 2025 TeamGemba</span>
        <Link href="/om" className="hover:text-sand-600 transition-colors">Om TeamGemba</Link>
      </footer>

      {/* ── FORM MODAL ── */}
      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-sand-900/30 backdrop-blur-sm"
            onClick={() => setFormOpen(false)}
          />

          <div className="relative w-full max-w-md bg-white rounded-3xl shadow-warm-xl border border-transparent p-8 animate-slide-up max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setFormOpen(false)}
              className="absolute top-4 right-4 p-1.5 text-sand-400 hover:text-sand-700 hover:bg-sand-100 rounded-lg transition-colors"
            >
              <X size={16} />
            </button>

            {submitted ? (
              <div className="text-center py-4">
                <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle size={28} className="text-green-500" />
                </div>
                <h2 className="text-xl font-bold text-sand-900 mb-2">Du er på listen!</h2>
                <p className="text-sand-500 text-sm leading-relaxed">
                  Tak, <strong>{name}</strong>! Vi skriver til <strong>{email}</strong>, når din plads er klar.
                </p>
                <button
                  onClick={() => setFormOpen(false)}
                  className="mt-6 px-5 py-2.5 bg-amber-500 text-white text-sm font-semibold rounded-xl hover:bg-amber-400 transition-colors"
                >
                  Luk
                </button>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <h2 className="text-xl font-bold text-sand-900 mb-1">Prøv TeamGemba</h2>
                  <p className="text-sm text-sand-500">Vi skriver til dig, når din plads er klar.</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-sand-700 mb-1.5">
                      Dit navn <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => { setName(e.target.value); setErrors((v) => ({ ...v, name: undefined })); }}
                      placeholder="Alex Rivera"
                      className={`w-full px-3.5 py-2.5 border rounded-xl text-sm bg-white text-sand-800 placeholder:text-sand-300 focus:outline-none focus:ring-2 focus:ring-amber-400 transition-shadow ${errors.name ? 'border-red-300' : 'border-sand-300'}`}
                    />
                    {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-sand-700 mb-1.5">
                      Arbejds-e-mail <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setErrors((v) => ({ ...v, email: undefined })); }}
                      placeholder="alex@virksomhed.dk"
                      className={`w-full px-3.5 py-2.5 border rounded-xl text-sm bg-white text-sand-800 placeholder:text-sand-300 focus:outline-none focus:ring-2 focus:ring-amber-400 transition-shadow ${errors.email ? 'border-red-300' : 'border-sand-300'}`}
                    />
                    {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-sand-700 mb-1.5">
                      Virksomhed <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <Building2 size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sand-400" />
                      <input
                        type="text"
                        value={company}
                        onChange={(e) => { setCompany(e.target.value); setErrors((v) => ({ ...v, company: undefined })); }}
                        placeholder="Virksomhed A/S"
                        className={`w-full pl-9 pr-3.5 py-2.5 border rounded-xl text-sm bg-white text-sand-800 placeholder:text-sand-300 focus:outline-none focus:ring-2 focus:ring-amber-400 transition-shadow ${errors.company ? 'border-red-300' : 'border-sand-300'}`}
                      />
                    </div>
                    {errors.company && <p className="mt-1 text-xs text-red-500">{errors.company}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-sand-700 mb-1.5">
                      Teamstørrelse <span className="text-sand-400 font-normal">(valgfrit)</span>
                    </label>
                    <div className="relative">
                      <Users size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sand-400" />
                      <select
                        value={teamSize}
                        onChange={(e) => setTeamSize(e.target.value)}
                        className="w-full pl-9 pr-3.5 py-2.5 border border-sand-300 rounded-xl text-sm bg-white text-sand-800 focus:outline-none focus:ring-2 focus:ring-amber-400 appearance-none"
                      >
                        <option value="">Vælg teamstørrelse</option>
                        <option value="1-5">1–5 personer</option>
                        <option value="6-15">6–15 personer</option>
                        <option value="16-50">16–50 personer</option>
                        <option value="50+">50+ personer</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className={`flex items-start gap-3 cursor-pointer p-3 rounded-xl border transition-colors ${errors.consent ? 'border-red-300 bg-red-50/50' : 'border-sand-200 hover:bg-sand-50'}`}>
                      <div className="relative flex-shrink-0 mt-0.5">
                        <input
                          type="checkbox"
                          checked={marketingConsent}
                          onChange={(e) => { setMarketingConsent(e.target.checked); setErrors((v) => ({ ...v, consent: undefined })); }}
                          className="sr-only"
                        />
                        <div className={`w-[18px] h-[18px] rounded border-2 flex items-center justify-center transition-colors ${marketingConsent ? 'bg-amber-500 border-amber-500' : 'border-sand-300 bg-white'}`}>
                          {marketingConsent && (
                            <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                              <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                        </div>
                      </div>
                      <span className="text-xs text-sand-600 leading-relaxed">
                        Jeg accepterer at modtage mails om onboarding og produktopdateringer <span className="text-red-400">*</span>
                      </span>
                    </label>
                    {errors.consent && <p className="mt-1 text-xs text-red-500">{errors.consent}</p>}
                  </div>

                  {submitError && <p className="text-xs text-red-500">{submitError}</p>}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 bg-amber-500 hover:bg-amber-400 text-white text-sm font-bold rounded-xl disabled:opacity-60 transition-all flex items-center justify-center gap-2 shadow-warm"
                  >
                    {loading ? (
                      <><Loader2 size={16} className="animate-spin" />Sender...</>
                    ) : (
                      <>Prøv TeamGemba <ArrowRight size={15} /></>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
